import { Injectable, EventEmitter } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ProductClassificationModel } from '../models/productclassification.model';
import { ProductClass } from '../enums/productClass.enum';
import { FloorTypes } from '../enums/floorTypes.enum';
import { FloorType } from '../models/floorType.model';
import { ViewboxOptionWithPrice } from '../models/wallFinishType.model';
import { FinishTypes } from '../enums/finishTypes.enum';
import { HingeType } from '../enums/hingeType.enum';
import { DoorHandleType } from '../enums/doorHandleType.enum';
import { ConfigurationService } from './configuration.service';
import { Configuration } from '../models/configuration.model';
import { WallSide } from '../enums/wallSide.enum';
import { WallFinish } from '../models/wallFinish.model';
import { DoorType } from '../enums/doorType.enum';

@Injectable({
  providedIn: 'root'
})
export class ProductClassificationService {
  private useLocalUrl = false;
  private settingsFileName = 'productclassification.json';
  private localProductClassificationUrl = '/assets/' + this.settingsFileName;
  private baseGithubRepositoryUrl = "https://api.github.com/repos/";
  private githubRepositoryOwnerName = "SosthenesNL";
  private githubRepositoryName = "modularSettings";
  private productClassificationModel: ProductClassificationModel;
  private cellProductClass: ProductClass = ProductClass.STANDARD;
  private configurationService: ConfigurationService;

  classificationReceived$: EventEmitter<boolean> = new EventEmitter<boolean>();

  constructor(private http: HttpClient) {
    if (this.useLocalUrl) {
      this.GetLocalProductClassificationModel();
    } else {
      this.GetGithubRepositoryFile();
    }
  }

  private GetLocalProductClassificationModel() {
    const httpHeaders = new HttpHeaders();
    this.http.get<ProductClassificationModel>(this.localProductClassificationUrl, {headers: httpHeaders}).subscribe(data => {
      this.productClassificationModel = data as ProductClassificationModel;
      this.classificationReceived$.emit(true);
    });
  }

  private GetGithubRepositoryFile() {
    let githubRepositoryhttpHeaders = new HttpHeaders();
    githubRepositoryhttpHeaders = githubRepositoryhttpHeaders.set('Accept', 'application/vnd.github.raw+json');
    githubRepositoryhttpHeaders = githubRepositoryhttpHeaders.set('X-GitHub-Api-Version', '2022-11-28');
    const url = this.baseGithubRepositoryUrl + this.githubRepositoryOwnerName + "/" +
      this.githubRepositoryName + "/contents/" + this.settingsFileName;

    this.http.get<any>(url,
      {
        headers: githubRepositoryhttpHeaders,
        responseType: 'json'
      }).subscribe({
        next: (filedata) => {
          this.productClassificationModel = filedata as ProductClassificationModel;
          this.classificationReceived$.emit(true);
        },
        error: (error) => {
          console.error("Error getting file from github repository");
          console.error(error);
          console.error("Falling back to local file");
          this.useLocalUrl = true;
          this.GetLocalProductClassificationModel();
        }
      });
  }

  private isStandardWallThickness(wallThickness: number) {
    const proWallThickness = this.productClassificationModel.proWallThickness;
    const proPlusWallThickness = this.productClassificationModel.proPlusWallThickness;
    return !proWallThickness.includes(wallThickness) && !proPlusWallThickness.includes(wallThickness);
  }

  public GetFloorClassification(floortype: FloorTypes): ProductClass {
    const floorClassification = this.productClassificationModel.FloorTypes.find(fc => fc.floorType === floortype);
    if (!floorClassification) { return ProductClass.STANDARD; }
    const classes: string[] = Object.values(ProductClass);
    if (classes.includes(floorClassification.productClass)) {
      return floorClassification.productClass;
    } else {
      return ProductClass.STANDARD;
    }
  }

  public SetFloorClassification(floors: FloorType[]) {
    floors.forEach(floor => {
      const productClass = this.GetFloorClassification(floor.option);
      if (productClass !== ProductClass.STANDARD) {
        floor.productClass = productClass;
      }
    });
  }

  public GetFinishClassification(finishType: FinishTypes): ProductClass {
    const finishClassification = this.productClassificationModel.FinishTypes.find(fc => fc.finishType === finishType);
    if (!finishClassification) { return ProductClass.STANDARD; }
    const classes: string[] = Object.values(ProductClass);
    if (classes.includes(finishClassification.productClass)) {
      return finishClassification.productClass;
    } else {
      return ProductClass.STANDARD;
    }
  }

  public SetFinishClassification(finishes: ViewboxOptionWithPrice<FinishTypes>[]) {
    finishes.forEach(finish => {
      const productClass = this.GetFinishClassification(finish.option);
      if (productClass !== ProductClass.STANDARD) {
        finish.productClass = productClass;
      }
    });
  }

  public GetHingeClassification(hingeType: HingeType): ProductClass {
    const hingeClassification = this.productClassificationModel.HingeTypes.find(fc => fc.hingeType === hingeType);
    if (!hingeClassification) { return ProductClass.STANDARD; }
    const classes: string[] = Object.values(ProductClass);
    if (classes.includes(hingeClassification.productClass)) {
      return hingeClassification.productClass;
    } else {
      return ProductClass.STANDARD;
    }
  }

  public SetHingeClassification(hinges: ViewboxOptionWithPrice<HingeType>[]) {
    hinges.forEach(hinge => {
      const productClass = this.GetHingeClassification(hinge.option);
      if (productClass !== ProductClass.STANDARD) {
        hinge.productClass = productClass;
      }
    });
  }

  public GetDoorHandleClassification(doorHandleType: string): ProductClass {
    const doorHandleClassification = this.productClassificationModel.DoorHandleTypes.find(fc => fc.doorHandleType === doorHandleType);
    if (!doorHandleClassification) { return ProductClass.STANDARD; }
    const classes: string[] = Object.values(ProductClass);
    if (classes.includes(doorHandleClassification.productClass)) {
      return doorHandleClassification.productClass;
    } else {
      return ProductClass.STANDARD;
    }
  }

  public SetDoorHandleClassification(doorHandles: ViewboxOptionWithPrice<DoorHandleType>[]) {
    doorHandles.forEach(doorHandle => {
      const productClass = this.GetDoorHandleClassification(doorHandle.option);
      if (productClass !== ProductClass.STANDARD) {
        doorHandle.productClass = productClass;
      }
    });
  }

  public GetProWallThickness(): number[] {
    return this.productClassificationModel.proWallThickness;
  }

  public GetProPlusWallThickness(): number[] {
    return this.productClassificationModel.proPlusWallThickness;
  }

  public GetInnerwallsProductClass(): ProductClass {
    return this.productClassificationModel.innerwallsProductClass;
  }

  public openClassificationURL(productClass: ProductClass) {
    if (productClass === ProductClass.STANDARD) { return; }
    let url = "";
    if (productClass === ProductClass.PRO) {
      if (this.productClassificationModel.proURL && this.productClassificationModel.proURL.length > 0) {
        url = this.productClassificationModel.proURL;
      } else {
        return;
      }
    } else {
      if (this.productClassificationModel.proPlusURL && this.productClassificationModel.proPlusURL.length > 0) {
        url = this.productClassificationModel.proPlusURL;
      } else {
        return;
      }
    }
    window.open(url);
  }

  public IsStandardCellHeight(cellHeight: number): boolean {
    return this.productClassificationModel.standardCellHeight.includes(cellHeight);
  }

  public isStandardCellSideMin(side: number): boolean {
    return side >= this.productClassificationModel.minSideLength;
  }

  public isStandardCellSideMax(side: number): boolean {
    return side <= this.productClassificationModel.maxOneSideLength;
  }

  public IsStandardCellSize(width: number, length: number): boolean {
    if (width < this.productClassificationModel.minSideLength) { return false;}
    if (length < this.productClassificationModel.minSideLength) { return false; }
    if (width >= this.productClassificationModel.maxOneSideLength &&
        length >= this.productClassificationModel.maxOneSideLength) { return false; }
    return true;
  }

  public GetDoorProductClassByWidth(width: number): ProductClass {
    if (this.productClassificationModel.standardDoorWidths.includes(width)) { return ProductClass.STANDARD; }
    return ProductClass.PRO;
  }

  public GetDoorProductClassByHeight(height: number): ProductClass {
    if (this.productClassificationModel.standardDoorHeights.includes(height)) { return ProductClass.STANDARD; }
    return ProductClass.PRO;
  }

  public IsStandardDoor(doorWidth: number, doorHeight: number): boolean {
    if (this.GetDoorProductClassByWidth(doorWidth) !== ProductClass.STANDARD) { return false; }
    if (this.GetDoorProductClassByHeight(doorHeight) !== ProductClass.STANDARD) { return false; }
    return true;
  }

  public GetCellProductClass(configuration: Configuration): ProductClass {
    let productClass = ProductClass.STANDARD;

    if (this.GetProWallThickness().includes(configuration.settings.wallThickness)) { productClass = ProductClass.PRO; }
    if (this.GetProPlusWallThickness().includes(configuration.settings.wallThickness)) { return ProductClass.PROPLUS; }

    if (!this.IsStandardCellHeight(configuration.settings.heightInMM)) { productClass = ProductClass.PRO; }
    if (!this.IsStandardCellSize(configuration.settings.widthInMM, configuration.settings.lengthInMM)) { productClass = ProductClass.PRO; }

    const floorClassification = this.GetFloorClassification(configuration.settings.floorType);
    if (floorClassification === ProductClass.PRO) { productClass = ProductClass.PRO; }
    if (floorClassification === ProductClass.PROPLUS) { return ProductClass.PROPLUS; }

    if (configuration.settings.innerWalls.length > 0) {
      productClass = ProductClass.PRO;
      for (let i = 0; i < configuration.settings.innerWalls.length; i++) {
        const innerWall = configuration.settings.innerWalls[i];
        if (this.GetFinishClassification(innerWall.exteriorFinish) === ProductClass.PROPLUS) { return ProductClass.PROPLUS; }
        if (this.GetFinishClassification(innerWall.interiorFinish) === ProductClass.PROPLUS) { return ProductClass.PROPLUS; }
      }
    }

    for (let i = 0; i < configuration.settings.doorSettings.length; i++) { 
      const doorSetting = configuration.settings.doorSettings[i];
      const doorModel = configuration.settings.doorSettings[i].doorModel;
      if (doorModel.type === DoorType.Slide) { productClass = ProductClass.PRO; }
      const innerFinishClassification = this.GetFinishClassification(doorSetting.leafInteriorFinish);
      if (innerFinishClassification === ProductClass.PRO) { productClass = ProductClass.PRO; }
      if (innerFinishClassification === ProductClass.PROPLUS) { return ProductClass.PROPLUS; }
      const outerFinishClassification = this.GetFinishClassification(doorSetting.leafExteriorFinish);
      if (outerFinishClassification === ProductClass.PRO) { productClass = ProductClass.PRO; }
      if (outerFinishClassification === ProductClass.PROPLUS) { return ProductClass.PROPLUS; }
      const innerHingeClassification = this.GetHingeClassification(doorSetting.hingeType);
      if (innerHingeClassification === ProductClass.PRO) { productClass = ProductClass.PRO; }
      if (innerHingeClassification === ProductClass.PROPLUS) { return ProductClass.PROPLUS; }
      const doorHandleClassification = this.GetDoorHandleClassification(doorSetting.doorHandleType);
      if (doorHandleClassification === ProductClass.PRO) { productClass = ProductClass.PRO; }
      if (doorHandleClassification === ProductClass.PROPLUS) { return ProductClass.PROPLUS; }
      if (!this.IsStandardDoor(doorSetting.doorModel.openingWidth, doorSetting.doorModel.openingHeight)) { productClass = ProductClass.PRO; }
    }

    const wallSides: (string | WallSide)[] = Object.values(WallSide);

    for (let i = 0; i < wallSides.length; i++) {
      const wallFinish: WallFinish = configuration.settings.wallFinishes[wallSides[i]];
      if (wallFinish) {
        const externalWallFinishClassification = this.GetFinishClassification(wallFinish.exterior);
        if (externalWallFinishClassification === ProductClass.PRO) { productClass = ProductClass.PRO; }
        if (externalWallFinishClassification === ProductClass.PROPLUS) { return ProductClass.PROPLUS; }
        const internalWallFinishClassification = this.GetFinishClassification(wallFinish.interior);
        if (internalWallFinishClassification === ProductClass.PRO) { productClass = ProductClass.PRO; }
        if (internalWallFinishClassification === ProductClass.PROPLUS) { return ProductClass.PROPLUS; }
      }
    }

    return productClass;
  }

  public GetDeliveryTimeInWeeks(productClass: ProductClass): number {
    let deliveryTimeInWeeks = this.productClassificationModel.standardDeliveryWeeks;
    if (productClass === ProductClass.PRO) { deliveryTimeInWeeks = this.productClassificationModel.proDeliveryWeeks; }
    if (productClass === ProductClass.PROPLUS) { deliveryTimeInWeeks = this.productClassificationModel.proPlusDeliveryWeeks; }
    return deliveryTimeInWeeks;
  }

}
